import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { DetalleOT, CatalogCategory, CatalogSubcategory, CatalogDetail, EvidenciaImg, CustomGarage, AprobacionOtContext } from "../../types/ot.types";
import { FileUploaderPreview } from "../FileUploaderPreview";

interface Step5Props {
    detalleOt: any;
    selectedGarage?: CustomGarage;
    archivoCotizacion?: EvidenciaImg;
    detallesOT: DetalleOT[];
    categories: CatalogCategory[];
    subcategoriesCache: Record<string, CatalogSubcategory[]>;
    detailsCache: Record<string, CatalogDetail[]>;
    updateDetalle: (id: number, field: keyof DetalleOT, value: any) => void;
    evidenciasReparacion?: Record<number, EvidenciaImg[]>;
    isCompleted?: boolean;
    readOnly?: boolean;
    userRole?: string;
    aprobacionOt?: AprobacionOtContext | null;
    uploadVoBoDocument: (detalleId: number | undefined, file: File) => Promise<boolean>;
    rechazarOrdenAEjecutivo: (motivo: string, tipoRechazo: number) => Promise<void>;
    voboGeneralUrl?: string;
    voboGeneralFile?: File;
    ordenId?: string;
}

export const Step5AprobacionCliente: React.FC<Step5Props> = ({
    detalleOt, selectedGarage, archivoCotizacion,
    detallesOT, categories, subcategoriesCache, detailsCache,
    updateDetalle, evidenciasReparacion, isCompleted = false,
    readOnly = false, userRole = "", aprobacionOt = null, uploadVoBoDocument, rechazarOrdenAEjecutivo,
    voboGeneralUrl, voboGeneralFile, ordenId = ""
}) => {
    const [collapsedItems, setCollapsedItems] = useState<Record<number, boolean>>({});
    const [previewData, setPreviewData] = useState<{ url: string; type: string; name: string; } | null>(null);
    const [stagedFiles, setStagedFiles] = useState<Record<number, { file: File, url: string }>>({});
    const [isUploading, setIsUploading] = useState<Record<number, boolean>>({});
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [rejectType, setRejectType] = useState<number>(1);
    const [isRejecting, setIsRejecting] = useState(false);

    const isEjecutivo = userRole === "Ejecutivo";
    const isSysAdmin = userRole === "SYSADMIN";
    const esPreautorizacion =
      aprobacionOt?.etapa_actual === "PREAUTORIZACION_PREVENTIVO" ||
      aprobacionOt?.etapa_actual === "BYPASS_PREVENTIVO";
    const puedeAprobar =
      !!aprobacionOt?.puede_actuar ||
      !!aprobacionOt?.puede_aprobar ||
      isSysAdmin ||
      (esPreautorizacion && isEjecutivo);
    const puedeRechazarEjecutivo =
      (!!aprobacionOt?.puede_rechazar_ejecutivo ||
        (puedeAprobar && !esPreautorizacion && !isEjecutivo)) &&
      !isCompleted &&
      !esPreautorizacion;
    const historial = Array.isArray(aprobacionOt?.historial) ? aprobacionOt!.historial! : [];
    const montoAutorizado = Number(aprobacionOt?.monto_autorizado ?? 0);
    const topeN1 = Number(aprobacionOt?.tope_n1 ?? 15000);
    const topeN2 = Number(aprobacionOt?.tope_n2 ?? 50000);

    useEffect(() => {
        document.body.style.overflow = previewData || showRejectModal ? "hidden" : "auto";
        return () => { document.body.style.overflow = "auto"; };
    }, [previewData, showRejectModal]);

    const toggleCollapse = (id: number) => setCollapsedItems((prev) => ({ ...prev, [id]: !prev[id] }));

    const handleFileStage = (id: number, e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) setStagedFiles(prev => ({ ...prev, [id]: { file, url: URL.createObjectURL(file) } }));
    };

    const handleConfirmUpload = async (id: number) => {
        const confirmacion = globalThis.confirm("¿Estás seguro de subir este documento como el Visto Bueno definitivo del cliente? Esta acción notificará al aprobador correspondiente.");
        if (confirmacion && stagedFiles[id]) {
            setIsUploading(prev => ({ ...prev, [id]: true }));
            const success = await uploadVoBoDocument(id === 0 ? undefined : id, stagedFiles[id].file);
            if (success) {
                setStagedFiles(prev => { const newState = { ...prev }; delete newState[id]; return newState; });
            }
            setIsUploading(prev => ({ ...prev, [id]: false }));
        }
    };

    const handleRechazarOT = async () => {
        if (!rejectReason.trim()) return;
        setIsRejecting(true);
        await rechazarOrdenAEjecutivo(rejectReason, rejectType);
        setIsRejecting(false);
        setShowRejectModal(false);
        setRejectReason("");
        setRejectType(1);
    };

    const aprobados = detallesOT.filter((d) => d.aprobacion_cliente === "aprobado");
    const rechazados = detallesOT.filter((d) => d.aprobacion_cliente === "rechazado");
    const pendientes = detallesOT.filter((d) => d.aprobacion_cliente === "pendiente");
    const tieneVoboGeneral = !!voboGeneralUrl;
    const todosConceptosTienenVobo = detallesOT.every(d => !!d.voboUrl);
    const faltanVobos = !tieneVoboGeneral && !todosConceptosTienenVobo;

    // CÁLCULO AJUSTADO CON ISR
    const calcularTotal = (detalles: DetalleOT[]) =>
        detalles.reduce((acc, d) => {
            const cant = parseFloat(d.cantidad.toString()) || 0;
            const prec = parseFloat(d.precio_unitario_sin_iva.toString()) || 0;
            const iva = (parseFloat(d.porcentaje_iva.toString()) || 0) / 100;
            const isr = (parseFloat(d.porcentaje_isr?.toString()) || 0) / 100;
            const sub = cant * prec;
            return acc + (sub + (sub * iva) - (sub * isr));
        }, 0);

    return (
        <div id="ot-step5-resumen" className="row text-start position-relative">
            <div className="col-12 mb-4">
                <div className="card shadow-sm border-info">
                    <div className="card-header bg-info bg-opacity-10 fw-bold text-dark py-2">
                        <i className="fas fa-clipboard-list text-info me-2"></i> Resumen de la Orden
                    </div>
                    <div className="card-body py-3 small">
                        {(aprobacionOt?.etapa_actual_label || aprobacionOt?.nivel_requerido_label) && (
                            <div className="alert alert-light border mb-3 py-2">
                                <div className="d-flex flex-wrap gap-3 align-items-center">
                                    <div>
                                        <span className="text-muted">Etapa activa: </span>
                                        <span className="fw-bold text-primary">{aprobacionOt?.etapa_actual_label || aprobacionOt?.nivel_requerido_label}</span>
                                    </div>
                                    <div>
                                        <span className="text-muted">Monto cotizado: </span>
                                        <span className="fw-bold">${Number(aprobacionOt?.monto_cotizado || calcularTotal(detallesOT)).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                                    </div>
                                    <div>
                                        <span className="text-muted">Monto autorizado: </span>
                                        <span className="fw-bold text-success">${montoAutorizado.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                                    </div>
                                </div>
                                {!esPreautorizacion && !(aprobacionOt?.aprobacion_completa ?? aprobacionOt?.cascada_completa) && (
                                    <div className="text-muted mt-2 small">
                                        <i className="fas fa-info-circle me-1"></i>
                                        Tras cerrar este nivel, solo se solicitará el siguiente si el monto autorizado supera el umbral
                                        (Admin Especial &gt; ${topeN1.toLocaleString('es-MX')}; Subdirector ≥ ${topeN2.toLocaleString('es-MX')}).
                                        La OC se genera al completar los niveles de aprobación requeridos.
                                    </div>
                                )}
                                {esPreautorizacion && (
                                    <div className="text-success mt-2 small">
                                        <i className="fas fa-check-circle me-1"></i>
                                        Umbral de preautorización válido (mantenimiento preventivo): no requiere aprobación operativa. Al confirmar se generará la OC.
                                    </div>
                                )}
                            </div>
                        )}
                        {aprobacionOt?.motivo_solo_lectura && !isCompleted && !puedeAprobar && (
                            <div className="alert alert-warning py-2 mb-3">
                                <i className="fas fa-eye me-2"></i>{aprobacionOt.motivo_solo_lectura}
                            </div>
                        )}
                        {historial.length > 0 && (
                            <div className="mb-3">
                                <div className="fw-bold text-muted mb-2"><i className="fas fa-history me-1"></i> Historial de aprobaciones</div>
                                <ul className="list-group list-group-flush border rounded">
                                    {historial.map((ev: any) => (
                                        <li key={ev.clave_evento || `${ev.nivel}-${ev.fecha_alta}`} className="list-group-item py-2 px-3">
                                            <div className="d-flex justify-content-between gap-2 flex-wrap">
                                                <span className="fw-bold">{ev.nivel || 'N/D'}</span>
                                                <span className="text-muted">{ev.fecha_alta ? new Date(ev.fecha_alta).toLocaleString('es-MX') : ''}</span>
                                            </div>
                                            <div className="small">{ev.comentario || ev.accion}</div>
                                            {ev.nombre_completo && <div className="small text-muted">Por: {ev.nombre_completo}</div>}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        <div className="row text-start">
                            <div className="col-md-4 border-end">
                                <span className="text-muted d-block mb-1">Taller Asignado:</span>
                                <span className="fw-bold"><i className="fas fa-wrench me-1 text-secondary"></i> {selectedGarage?.nombre_completo || 'No asignado'}</span>
                            </div>
                            <div className="col-md-4 border-end">
                                <span className="text-muted d-block mb-1">Fechas:</span>
                                <div><i className="fas fa-calendar-check me-1 text-secondary"></i> <strong>Ingreso:</strong> {detalleOt?.fecha_recepcion_bien ? new Date(detalleOt.fecha_recepcion_bien).toLocaleString('es-MX') : 'N/A'}</div>
                                <div><i className="fas fa-flag-checkered me-1 text-secondary"></i> <strong>Estimada:</strong> {detalleOt?.fecha_estimada_entrega ? new Date(detalleOt.fecha_estimada_entrega).toLocaleString('es-MX') : 'N/A'}</div>
                            </div>
                            <div className="col-md-4">
                                <span className="text-muted d-block mb-1">Cotización:</span>
                                <div className="fw-bold text-success fs-6 mb-1">${calcularTotal(detallesOT).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                {archivoCotizacion?.url ? (
                                    <button type="button" className="btn btn-sm btn-outline-primary py-0 px-2 mt-1" onClick={() => setPreviewData({ url: archivoCotizacion.url!, type: "pdf", name: "Cotización" })}>
                                        <i className="fas fa-file-pdf me-1"></i> Ver Cotización PDF
                                    </button>
                                ) : <span className="text-muted fst-italic">Sin documento</span>}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {puedeAprobar && !isCompleted && faltanVobos && (
                <div className="col-12 mb-3">
                    <div className="alert alert-danger shadow-sm d-flex align-items-center">
                        <i className="fas fa-ban fa-2x me-3 text-danger"></i>
                        <div><h6 className="fw-bold mb-1">Acción Requerida</h6><p className="mb-0 small">No puedes avanzar hasta que se suba el VoBo del cliente (General o por concepto).</p></div>
                    </div>
                </div>
            )}

            <div className="col-12 mb-4">
                <div className={`card border-2 ${tieneVoboGeneral ? 'border-success' : 'border-primary'} shadow-sm`}>
                    <div className="card-body p-3">
                        <h6 className="fw-bold mb-2"><i className="fas fa-file-signature me-2 text-primary"></i> VoBo General de la Orden</h6>
                        <div className="d-flex align-items-center gap-3">
                            <input type="file" className="form-control form-control-sm w-auto" onChange={(e) => handleFileStage(0, e)} disabled={(voboGeneralUrl != null && voboGeneralUrl != "") || isCompleted || isUploading[0]} accept="image/*,application/pdf" />
                            {stagedFiles[0] && (
                                <button className="btn btn-primary btn-sm px-4 fw-bold" type="button" onClick={() => handleConfirmUpload(0)} disabled={isUploading[0]}>
                                    {isUploading[0] ? "Subiendo..." : "Confirmar Archivo"}
                                </button>
                            )}
                            {voboGeneralUrl && (
                                <button className="btn btn-outline-success btn-sm fw-bold" type="button" onClick={() => setPreviewData({ url: voboGeneralUrl, type: voboGeneralFile?.type || "pdf", name: voboGeneralFile?.name || "VoBo General" })}>
                                    <i className="fas fa-eye me-1"></i> Ver Documento
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="col-12 mb-4 d-flex justify-content-between align-items-center flex-wrap gap-3">
                <div className="border rounded px-4 py-3 text-muted small flex-grow-1"><i className="fas fa-info-circle me-2 text-primary"></i>Revise y seleccione <strong>Aprobar</strong> o <strong>Rechazar</strong>.</div>
                {puedeRechazarEjecutivo && (
                    <button type="button" className="btn btn-outline-danger fw-bold shadow-sm" onClick={() => setShowRejectModal(true)}><i className="fas fa-undo-alt me-2"></i> Rechazar a Ejecutivo</button>
                )}
            </div>

            <div className="col-12">
                {detallesOT.map((detalle, idx) => {
                    // CÁLCULO AJUSTADO CON ISR
                    const cantidad = parseFloat(detalle.cantidad.toString()) || 0;
                    const precio_sin_iva = parseFloat(detalle.precio_unitario_sin_iva.toString()) || 0;
                    const iva_rate = (parseFloat(detalle.porcentaje_iva.toString()) || 0) / 100;
                    const isr_rate = (parseFloat(detalle.porcentaje_isr?.toString()) || 0) / 100;

                    const subtotal = cantidad * precio_sin_iva;
                    const total_cotizado = subtotal + (subtotal * iva_rate) - (subtotal * isr_rate);

                    const isCollapsed = collapsedItems[detalle.id];
                    const estadoActual = detalle.aprobacion_cliente;

                    const catLabel = detalle.categoria_label || 'Sin Categoría';
                    const subLabel = detalle.subcategoria_label || 'Sin Subcategoría';
                    const detLabel = detalle.subcategoria_detalle_label || 'Sin Detalle';
                    const evidenciasConcepto = evidenciasReparacion?.[detalle.id] || [];

                    let borderClass = estadoActual === "aprobado" ? "border-success" : (estadoActual === "rechazado" ? "border-danger" : "border-secondary");

                    return (
                        <div key={detalle.id} className={`card shadow-none mb-3 border border-start border-4 ${borderClass}`}>
                            <div className="card-header py-3 d-flex justify-content-between align-items-center border-bottom-0">
                                <div>
                                    <span className="text-muted fw-bold me-2 small">#{idx + 1}</span>
                                    <span className="fw-bold">{catLabel}</span> <span className="mx-2 text-muted">&gt;</span> <span className="text-muted small">{subLabel}</span> <span className="mx-2 text-muted">&gt;</span> <span className="text-muted small">{detLabel}</span>
                                </div>
                                <div className="text-end">
                                    <div className="fw-bold fs-5 mb-2">${total_cotizado.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                    <button type="button" className="btn btn-sm btn-light border text-muted px-3" onClick={() => toggleCollapse(detalle.id)}>
                                        {isCollapsed ? "Expandir" : "Ocultar"}
                                    </button>
                                </div>
                            </div>
                            {!isCollapsed && (
                                <div className="card-body pt-0 pb-4 px-4 border-top">
                                    <div className="row mt-3">
                                        <div className="col-md-7 border-end pe-md-4">
                                            <h6 className="fw-bold mb-2 small text-uppercase">Descripción y Evidencias</h6>
                                            <p className="text-muted small mb-3">{detalle.observaciones || "Sin descripción detallada."}</p>
                                            {evidenciasConcepto.length > 0 ? <FileUploaderPreview label="" archivos={evidenciasConcepto} setArchivos={() => { }} disabled={true} /> : <span className="small text-muted">Sin evidencia.</span>}
                                        </div>
                                        <div className="col-md-5 ps-md-4 mt-4 mt-md-0 d-flex flex-column">
                                            <div className="mb-4 bg-light p-3 rounded border shadow-sm">
                                                <label className="fw-bold small mb-2 d-block text-primary"><i className="fas fa-file-upload me-1"></i> VoBo Específico</label>
                                                {detalle.voboUrl && !stagedFiles[detalle.id] && (
                                                    <button type="button" className="btn btn-sm btn-outline-success w-100" onClick={() => setPreviewData({ url: detalle.voboUrl!, type: "pdf", name: "VoBo" })}><i className="fas fa-eye"></i> Ver Actual</button>
                                                )}
                                                {(!detalle.voboUrl || stagedFiles[detalle.id]) && (
                                                    <div className="d-flex flex-column gap-2 mt-2">
                                                        <input type="file" className="form-control form-control-sm" accept="image/*,application/pdf" disabled={isCompleted} onChange={(e) => handleFileStage(detalle.id, e)} />
                                                        {stagedFiles[detalle.id] && <button type="button" className="btn btn-primary btn-sm fw-bold" onClick={() => handleConfirmUpload(detalle.id)}>Subir VoBo</button>}
                                                    </div>
                                                )}
                                            </div>

                                            <h6 className="fw-bold mb-2 small text-uppercase">Resolución</h6>
                                            <div className="d-flex gap-2 mb-3">
                                                <button type="button" disabled={readOnly || isCompleted || !puedeAprobar} className={`btn btn-sm flex-fill ${estadoActual === "aprobado" ? "btn-success" : "btn-outline-secondary"}`} onClick={() => { updateDetalle(detalle.id, "aprobacion_cliente", "aprobado"); updateDetalle(detalle.id, "motivo_rechazo_cliente", ""); }}>Aprobar</button>
                                                <button type="button" disabled={readOnly || isCompleted || !puedeAprobar} className={`btn btn-sm flex-fill ${estadoActual === "rechazado" ? "btn-danger" : "btn-outline-secondary"}`} onClick={() => updateDetalle(detalle.id, "aprobacion_cliente", "rechazado")}>Rechazar</button>
                                            </div>
                                            {estadoActual === "rechazado" && (
                                                <textarea className="form-control form-control-sm border-danger" rows={2} placeholder="Motivo del rechazo..." value={detalle.motivo_rechazo_cliente || ""} onChange={(e) => updateDetalle(detalle.id, "motivo_rechazo_cliente", e.target.value)} disabled={readOnly || isCompleted || !puedeAprobar} required />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {showRejectModal && createPortal(
                <>
                    <div className="modal-backdrop fade show" style={{ zIndex: 1050 }}></div>
                    <div className="modal d-block fade show" tabIndex={-1} style={{ zIndex: 1055 }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content border-danger shadow-lg">
                                <div className="modal-header bg-danger text-white">
                                    <h5 className="modal-title fw-bold"><i className="fas fa-exclamation-triangle me-2"></i> Rechazar Orden al Ejecutivo</h5>
                                    <button type="button" className="btn-close btn-close-white" onClick={() => setShowRejectModal(false)}></button>
                                </div>
                                <div className="modal-body text-start">
                                    <div className="mb-4">
                                        <label className="form-label fw-bold text-primary mb-3"><i className="fas fa-route me-2"></i>¿A qué paso deseas devolver la orden?</label>

                                        <div className={`form-check p-3 border rounded mb-2 ${rejectType === 1 ? 'bg-light border-primary' : ''}`} onClick={() => !isRejecting && setRejectType(1)} style={{ cursor: 'pointer' }}>
                                            <input className="form-check-input ms-1" type="radio" name="rejectType" checked={rejectType === 1} onChange={() => setRejectType(1)} disabled={isRejecting} />
                                            <label className="form-check-label ms-2 d-block" style={{ cursor: 'pointer' }}>
                                                <span className="fw-bold text-dark">Diagnóstico y Costos (Paso 3)</span><br />
                                                <span className="small text-muted">El ejecutivo deberá corregir conceptos, precios o cantidades.</span>
                                            </label>
                                        </div>

                                        <div className={`form-check p-3 border rounded ${rejectType === 2 ? 'bg-light border-primary' : ''}`} onClick={() => !isRejecting && setRejectType(2)} style={{ cursor: 'pointer' }}>
                                            <input className="form-check-input ms-1" type="radio" name="rejectType" checked={rejectType === 2} onChange={() => setRejectType(2)} disabled={isRejecting} />
                                            <label className="form-check-label ms-2 d-block" style={{ cursor: 'pointer' }}>
                                                <span className="fw-bold text-dark">Solo Evidencias (Paso 4)</span><br />
                                                <span className="small text-muted">Los costos quedan bloqueados. El ejecutivo solo podrá subir fotografías faltantes.</span>
                                            </label>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="form-label fw-bold text-dark small">Motivo de la corrección:</label>
                                        <textarea className="form-control" rows={3} placeholder="Especifique qué debe corregir el ejecutivo..." value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} disabled={isRejecting}></textarea>
                                    </div>
                                </div>
                                <div className="modal-footer bg-light">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowRejectModal(false)} disabled={isRejecting}>Cancelar</button>
                                    <button type="button" className="btn btn-danger fw-bold" onClick={handleRechazarOT} disabled={isRejecting || !rejectReason.trim()}>{isRejecting ? "Procesando..." : "Confirmar Rechazo"}</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>, document.body
            )}

            {previewData && createPortal(
                <>
                    <div className="modal-backdrop fade show" style={{ zIndex: 1050, backgroundColor: "rgba(0,0,0,0.8)" }} onClick={() => setPreviewData(null)}></div>
                    <div className="modal d-block fade show" tabIndex={-1} style={{ zIndex: 1055, overflowY: "hidden" }} onClick={() => setPreviewData(null)}>
                        <div className="modal-dialog modal-dialog-centered modal-xl" style={{ height: "90vh", maxWidth: "90vw" }} onClick={(e) => e.stopPropagation()}>
                            <div className="modal-content shadow-lg border-0 bg-transparent h-100 w-100">
                                <div className="modal-header bg-white py-2 rounded-top">
                                    <h6 className="modal-title fw-bold text-truncate">Vista Previa</h6>
                                    <button type="button" className="btn-close" onClick={() => setPreviewData(null)}></button>
                                </div>
                                <div className="modal-body p-0 bg-dark rounded-bottom d-flex justify-content-center align-items-center" style={{ height: "100%", position: "relative" }}>
                                    <a href={previewData.url} download={previewData.name || "evidencia_vobo"} className="btn btn-light position-absolute shadow-sm" style={{ bottom: "20px", right: "20px", zIndex: 100 }}><i className="fas fa-download me-2"></i> Descargar</a>
                                    {previewData.type.includes("pdf") ? <embed src={previewData.url} type="application/pdf" width="100%" height="100%" /> : <img src={previewData.url} alt="Preview" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />}
                                </div>
                            </div>
                        </div>
                    </div>
                </>, document.body
            )}
        </div>
    );
};